import { useCallback, useEffect, useRef, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { greeting, shouldRefreshOnReturn, todayLabel } from "../lib/dashboard";
import { BLOCK_COMPONENTS } from "../components/dashboard/DashboardBlocks";
import AccessNotice from "../components/AccessNotice";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { LayoutDashboard, RefreshCw } from "lucide-react";

// Tableau de bord (`Docs/Fondations/V1/PROPOSITION_TABLEAU_DE_BORD.md`): open to every account. The server lists the blocks this
// person may see (module + permission), then each block loads on its own. « À traiter » first, full width; no background polling —
// « Actualiser », and a refresh when the tab comes back into view (at most once a minute).
export default function Dashboard() {
  const { user } = useAuth();
  const [blocks, setBlocks] = useState(null);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const loadedAt = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/blocks");
      setBlocks(data.filter((b) => BLOCK_COMPONENTS[b.id]));
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger le tableau de bord."));
    }
    loadedAt.current = Date.now();
  }, []);
  const refresh = useCallback(() => {
    loadList();
    setVersion((v) => v + 1);
  }, [loadList]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && shouldRefreshOnReturn(loadedAt.current)) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Tableau de bord</h1>
          <p className="text-sm text-slate-500">{greeting(user?.name)} · {todayLabel()}</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="btn-refresh-stats">
          <RefreshCw size={14} className="mr-2" />Actualiser
        </Button>
      </div>
      <AccessNotice />

      {blocks === null && !error && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="dashboard-loading">
          <Skeleton className="h-28 lg:col-span-2" /><Skeleton className="h-64" /><Skeleton className="h-64" />
        </div>
      )}
      {blocks === null && error && (
        <Card className="p-8 text-center space-y-3" data-testid="dashboard-error">
          <p className="text-sm text-slate-600">{error}</p>
          <Button variant="outline" size="sm" onClick={loadList}>Réessayer</Button>
        </Card>
      )}
      {blocks && blocks.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="dashboard-empty">
          <LayoutDashboard className="mx-auto text-slate-300" size={26} />
          <h2 className="font-medium text-slate-900">Rien à afficher pour votre profil</h2>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Le tableau de bord montre les indicateurs des modules auxquels votre rôle donne accès. Utilisez le menu pour rejoindre vos pages.
          </p>
        </Card>
      )}
      {blocks && blocks.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="dashboard-blocks">
          {blocks.map((b) => {
            const Block = BLOCK_COMPONENTS[b.id];
            return <Block key={b.id} version={version} />;
          })}
        </div>
      )}
    </div>
  );
}
